import { ComponentProps } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { JiraLogo } from '@atlaskit/logo';

import { WhiteTemplate } from '@/components/signup/layouts/white-template';
import { UpgradeEligibleUser } from '@/components/signup/site-activation/components/upgrade-eligible-user/upgrade-eligible-user';
import { SiteActivation } from '@/components/signup/site-activation/site-activation';
import { getFirstName } from '@/components/signup/utils/get-first-name';
import { useUserContext } from '@/components/user-context/user-context-provider';

import { InputEmail } from '../../../components/input-email';
import { NoCreditCardRequired } from '../../../components/no-credit-card-required';
import { SubmitButtonMessageNext } from '../../../components/submit-button-message-next';
import { useGetExistingSites } from '../../../utils/get-existing-sites/use-get-existing-sites';

export const LoggedInJiraWhiteTemplateUpgradeEligibleUser = (
	props: ComponentProps<typeof WhiteTemplate>,
) => {
	const { formatMessage } = useIntl();
	const {
		user: { name, email },
	} = useUserContext();

	// Upgradeable Standard Sites Experiment
	const existingSites = useGetExistingSites();
	const siteActivationContent = existingSites ? (
		<UpgradeEligibleUser SubmitButton={<SubmitButtonMessageNext />} />
	) : undefined;

	return (
		<WhiteTemplate
			{...props}
			productLogos={[<JiraLogo appearance="brand" size="xlarge" key="jira" />]}
		>
			<div css={containerCss}>
				<h2 css={siteActivationHeadingCss}>
					{name
						? formatMessage(
								{
									id: 'BxpUiSignup.nameSite.header',
									defaultMessage: 'Welcome back, {name}',
								},
								{ name: getFirstName(name) },
							)
						: formatMessage({
								id: 'JiraSoftwarePageTemplate.loggedInHero.welcomeBack',
								defaultMessage: 'Welcome back',
							})}
				</h2>
				<InputEmail email={email} />
				<SiteActivation
					existingSites={existingSites}
					UpgradeEligibleUser={siteActivationContent}
					CurrentProductUser={siteActivationContent}
					WeekOneUser={siteActivationContent ?? null}
				/>
				<div css={spacingStyles}>
					<NoCreditCardRequired />
				</div>
			</div>
		</WhiteTemplate>
	);
};

const containerCss = css({
	display: 'grid',
	gap: '8px',
});

const siteActivationHeadingCss = css({
	fontSize: 24,
	fontWeight: 500,
	margin: 0,
	textAlign: 'center',
});

const spacingStyles = css({
	marginBlockStart: '12px',
	marginBlockEnd: '24px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['p']: {
		margin: '12px 0 0',
	},
});
